import React from 'react'
import { Box, Stack, SvgIcon } from '@mui/material';
import { useSelection } from '../../hooks/use-selection';
import { CustomTable } from '../../components/custom-table';
import { CustomSearch } from '../../components/custom-search';
import { externalOrdersHeadCells } from '../../seed/table-headers';
import { importedOrderStatusForPaymentVerification, orderPaymentStatus, ROWSPERPAGE } from '../../utils/constant';
import { postRequest } from '../../services/api-service';
import { getAllExternalOrdersByPaginationUrl, updateExternalOrderPaymentStatusUrl } from '../../seed/url';
import EyeIcon from '@heroicons/react/24/outline/EyeIcon';
import { usePopover } from '../../hooks/use-popover';
import { handleExport } from "../Orders/ImportedOrders";
import ViewOrder from './ViewOrder';
import { useAuth } from '../../hooks/use-auth';
import { CustomPopOver } from '../../components/custom-popover';
import CircleStackIcon from '@heroicons/react/24/outline/CircleStackIcon';
import TrackOrder from '../Orders/TrackOrder';
import { useOrderTimeRange } from '../../hooks/use-order-time-range';
import { useStaffUsers } from '../../hooks/use-staff-users';
import { useVendors } from '../../hooks/use-vendors';
import { UserFilterAutocomplete } from '../../components/user-filter-autocomplete';
import { OrdersCostSummary } from '../../components/orders-cost-summary';
import { PageHeader } from '../../components/page-header';
import { StatusFilterButton } from '../../components/status-filter-button';
import { ResponsiveFilterBar } from '../../components/responsive-filter-bar';
import { useIsMobile } from '../../hooks/use-is-mobile';

const useProductsOrdersIds = (orders) => {
    return React.useMemo(
        () => {
            return orders.map((customer) => customer.id);
        },
        [orders]
    );
};

function ImportedOrdersPaymentVerification() {
    const auth = useAuth();
    const orderStatusPopOver = usePopover();
    const isMobile = useIsMobile();
    const [currentTab, setCurrentTab] = React.useState({ label: "All", value: "" });
    const [openViewDialog, setOpenViewDialog] = React.useState(false);
    const [openTrackOrderDialog, setOpenTrackOrderDialog] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWSPERPAGE);
    const [productsOrders, setProductsOrders] = React.useState({
        page: 1,
        total_results: 0,
        total_pages: 0,
        total_orders_product_cost: 0,
        total_orders_shipping_cost: 0,
        results: []
    });
    const [selectedData, setSelectedData] = React.useState({})
    const [searchTerm, setSearchTerm] = React.useState("")
    const [activeUserId, setActiveUserId] = React.useState(0);
    const [activeVendorId, setActiveVendorId] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false)
    const [exportExcel, setExportExcel] = React.useState(false)
    const productsIds = useProductsOrdersIds(productsOrders.results);
    const productsOrdersSelection = useSelection(productsIds);
    const { timeRange, dateFrom, dateTo, handleDateChange, filterItems } = useOrderTimeRange();
    const { users, isLoadingUsers } = useStaffUsers();
    const { vendors, isLoadingVendors } = useVendors();
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('id');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getDataForExportExcel = () => {
        setExportExcel(true)
        postRequest(
            getAllExternalOrdersByPaginationUrl,
            {
                query: searchTerm,
                status: currentTab.value,
                from: timeRange.from,
                to: timeRange.to,
                sort: orderBy + " " + order,
                limit: productsOrders.total_results,
                page: 1,
                user_id: activeUserId,
                vendor_id: activeVendorId,
            },
            (data) => {
                handleExport(data?.results)
                setExportExcel(false)
            },
            (error) => {
                setExportExcel(false)
            },
        )
    }

    const fetcher = React.useCallback(
        (page) => {
            setIsLoading(true)
            postRequest(
                getAllExternalOrdersByPaginationUrl,
                {
                    query: searchTerm,
                    status: currentTab.value,
                    from: timeRange.from,
                    to: timeRange.to,
                    sort: orderBy + " " + order,
                    limit: rowsPerPage,
                    page: page,
                    user_id: activeUserId,
                    vendor_id: activeVendorId,
                },
                (data) => {
                    setProductsOrders(data)
                    setIsLoading(false)
                },
                (error) => {
                    setProductsOrders({
                        page: 1,
                        total_results: 0,
                        total_pages: 0,
                        total_orders_product_cost: 0,
                        total_orders_shipping_cost: 0,
                        results: [],
                    })
                    setIsLoading(false)
                },
            )
        },
        [rowsPerPage, searchTerm, currentTab, timeRange, orderBy, order, activeUserId, activeVendorId]
    );

    const confirmSelectedOrderPaymentStatus = () => {
        productsOrdersSelection.selected.forEach((id, index) => {
            postRequest(
                updateExternalOrderPaymentStatusUrl,
                {
                    id: id,
                    user_id: auth?.user?.id
                },
                (data) => {
                    fetcher(productsOrders.page)
                },
                (error) => {
                    fetcher(productsOrders.page)
                },
            )
            if (productsOrdersSelection.selected.length === index + 1) {
                productsOrdersSelection.handleDeselectAll()
            }
        })
    }

    const handleSearch = (event) => {
        setSearchTerm(event.target.value)
    }

    React.useEffect(() => {
        fetcher(1)
    }, [fetcher])

    const handlePageChange = React.useCallback(
        (event, value) => {
            fetcher(value + 1)
        },
        [fetcher]
    );

    const handleRowsPerPageChange = React.useCallback(
        (event) => {
            setRowsPerPage(event.target.value);
        },
        []
    );

    const handleClickOpenViewDialog = () => {
        setOpenViewDialog(true)
    }

    const handleCloseViewDialog = () => {
        fetcher(productsOrders.page)
        setOpenViewDialog(false)
    }

    const handleClickOpenTrackOrderDialog = () => {
        setOpenTrackOrderDialog(true)
    }

    const handleCloseTrackOrderDialog = () => {
        setOpenTrackOrderDialog(false)
    }

    const onSelect = (data, openDialog) => {
        setSelectedData(data)
        openDialog && handleClickOpenViewDialog()
    }

    const orderPopoverItems = [
        // {
        //     id: 'view',
        //     label: 'View',
        //     icon: <SvgIcon fontSize="small" sx={{ color: "text.primary" }}><EyeIcon /></SvgIcon>,
        //     onClick: () => {
        //         dispatch({
        //             type: "TOOGLE_PAYMENT_SIDENAV",
        //             payload: {
        //                 ...orderSideNav,
        //                 openViewOrderSideNav: true,
        //                 orderSideNavContent: selectedData
        //             },
        //         });
        //     },
        // },
        {
            id: 'track_order',
            label: 'Track Order',
            icon: <SvgIcon fontSize="small" sx={{ color: "text.primary" }}><EyeIcon /></SvgIcon>,
            onClick: () => {
                handleClickOpenTrackOrderDialog()
            },
        }
    ]

    return (
        <>
            {orderStatusPopOver.open &&
                <CustomPopOver
                    id={orderStatusPopOver.id}
                    anchorEl={orderStatusPopOver.anchorRef}
                    open={orderStatusPopOver.open}
                    onClose={orderStatusPopOver.handleClose}
                    popoverItems={importedOrderStatusForPaymentVerification.map(item => {
                        return {
                            id: item.value,
                            label: item.label,
                            icon: <SvgIcon fontSize="small" sx={{ color: "primary.main" }}><CircleStackIcon /></SvgIcon>,
                            onClick: () => { setCurrentTab({ label: item.label, value: item.value }) }
                        }
                    })}
                />
            }
            {openViewDialog &&
                <ViewOrder
                    open={openViewDialog}
                    handleClose={handleCloseViewDialog}
                    data={selectedData}
                    url={updateExternalOrderPaymentStatusUrl}
                />
            }
            {openTrackOrderDialog &&
                <TrackOrder
                    open={openTrackOrderDialog}
                    handleClose={handleCloseTrackOrderDialog}
                    data={selectedData}
                />
            }
            <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
                <Stack spacing={2}>
                    <PageHeader title="Payments Verification" />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: isMobile ? "nowrap" : "wrap",
                            gap: isMobile ? 1 : 2,
                        }}
                    >
                        <StatusFilterButton
                            label={currentTab.label}
                            onClick={(event) => orderStatusPopOver.handleOpen(event)}
                        />
                        <ResponsiveFilterBar
                            activeCount={(activeUserId ? 1 : 0) + (activeVendorId ? 1 : 0)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2,
                                ml: { xs: 0, sm: 'auto' },
                            }}
                        >
                            <UserFilterAutocomplete
                                users={vendors}
                                isLoadingUsers={isLoadingVendors}
                                onChange={setActiveVendorId}
                                label="Vendor Name"
                                id="vendors-list"
                            />
                            <UserFilterAutocomplete
                                users={users}
                                isLoadingUsers={isLoadingUsers}
                                onChange={setActiveUserId}
                            />
                        </ResponsiveFilterBar>
                    </Box>
                    <CustomSearch
                        popoverItems={filterItems}
                        handleSearch={handleSearch}
                        exportExcel={exportExcel}
                        getDataForExportExcel={getDataForExportExcel}
                        selectedItems={productsOrdersSelection}
                        confirmSelectedOrderStatus={confirmSelectedOrderPaymentStatus}
                        statusOfOrder={orderPaymentStatus.slice(0, 1)}
                        from={dateFrom}
                        to={dateTo}
                        handleBodyChange={handleDateChange}
                    />
                    <OrdersCostSummary
                        productCost={productsOrders.total_orders_product_cost}
                        shippingCost={productsOrders.total_orders_shipping_cost}
                        isLoading={isLoading}
                    />
                    <CustomTable
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        count={productsOrders.total_results}
                        items={productsOrders.results}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onSelectOne={productsOrdersSelection.handleSelectOne}
                        onSelect={onSelect}
                        onSelectAll={productsOrdersSelection.handleSelectAll}
                        onDeselectOne={productsOrdersSelection.handleDeselectOne}
                        onDeselectAll={productsOrdersSelection.handleDeselectAll}
                        page={productsOrders.page >= 1 ? productsOrders.page - 1 : productsOrders.page}
                        rowsPerPage={rowsPerPage}
                        selected={productsOrdersSelection.selected}
                        headCells={externalOrdersHeadCells}
                        popoverItems={orderPopoverItems}
                        isLoading={isLoading}
                    />
                </Stack>
            </Box>
        </>
    )
}

export default ImportedOrdersPaymentVerification